import { useQuery } from '@tanstack/react-query';
import { api } from '../partage/api/client';
import type { Bulle, Maillon } from '../partage/api/types';
import { cles } from '../partage/relectures';
import { laMere, placeParmiLesSoeurs } from './ficheEnMots';
import { BULLE_PRECEDENTE, BULLE_SUIVANTE, FERMER_LA_FICHE, remonterA, surN } from '../partage/vocabulaire';

/**
 * La tête de la fiche, commune à l'ordinateur et au téléphone (chantier `revue-design`, ticket
 * 02 ; reprise telle quelle par la fiche du téléphone, chantier `telephone-projets-fiche`,
 * ticket 03) : « ‹ la mère » pour remonter, « 3 sur 6 ‹ › » pour passer aux sœurs, la croix.
 */
export function NavDeLaFiche({
  bulle,
  chemin,
  onMere,
  onSoeur,
  onFermer,
  avecCroix = true,
}: {
  bulle: Bulle;
  chemin: Maillon[];
  onMere?: () => void;
  onSoeur?: (id: string) => void;
  onFermer: () => void;
  /**
   * La croix au bout de la ligne : au téléphone. L'ordinateur la pose sur la ligne du haut, à côté
   * de « Réglages » (`HautDeLaFiche`, chantier types-de-fiche, modification 02).
   */
  avecCroix?: boolean;
}) {
  const mere = laMere(chemin);

  /*
   * La fratrie se lit sur la feuille du parent — la même clé que `cles.feuille`, donc le
   * plus souvent déjà en cache : ouvrir une fiche ne redemande rien au serveur.
   */
  const feuilleDuParent = useQuery({
    queryKey: cles.feuille(bulle.parentId),
    queryFn: () => api.feuille(bulle.parentId as string),
    enabled: !!bulle.parentId,
  });
  // Dans ton ordre (ton rang, puis l'arrivée), tel que le serveur le rend.
  const soeurs = (feuilleDuParent.data?.ordreHierarchique ?? []).map((id) => ({ id }));
  const place = placeParmiLesSoeurs(soeurs, bulle.id);

  return (
    <nav className="fiche-nav">
      {mere ? (
        <button
          type="button"
          className="fiche-mere"
          title={remonterA(mere.titre)}
          disabled={!onMere}
          onClick={onMere}
        >
          <Fleche sens="avant" />
          <span className="fiche-mere-titre">{mere.titre}</span>
        </button>
      ) : (
        <span />
      )}
      <div className="fiche-soeurs">
        {place && (
          <>
            <span className="fiche-soeurs-place">{surN(place.position, place.total)}</span>
            <button
              type="button"
              className="fiche-soeur"
              aria-label={BULLE_PRECEDENTE}
              disabled={!onSoeur || !place.precedente}
              onClick={() => place.precedente && onSoeur?.(place.precedente)}
            >
              <Fleche sens="avant" />
            </button>
            <button
              type="button"
              className="fiche-soeur"
              aria-label={BULLE_SUIVANTE}
              disabled={!onSoeur || !place.suivante}
              onClick={() => place.suivante && onSoeur?.(place.suivante)}
            >
              <Fleche sens="apres" />
            </button>
          </>
        )}
        {avecCroix && (
          <button
            type="button"
            className="fiche-fermer"
            data-tuto="fiche-fermer"
            onClick={onFermer}
            aria-label={FERMER_LA_FICHE}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>
    </nav>
  );
}

/** Le chevron des flèches de la tête : « ‹ » vers la mère et la sœur d'avant, « › » après. */
function Fleche({ sens }: { sens: 'avant' | 'apres' }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      <path
        d={sens === 'avant' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
